import { useState, type FormEvent, type ReactNode } from 'react';

import { api } from '../api/client';

import { DefiAntiRobot } from './DefiAntiRobot';

/**
 * « MOT DE PASSE OUBLIÉ ? » DANS LA CONSOLE — AB#747.
 *
 * L'écran de connexion de la console n'avait AUCUN lien de récupération : un
 * tuteur qui oubliait son mot de passe n'avait pas d'autre recours que
 * d'écrire à l'exploitant. La route existe depuis AB#614
 * (`POST /v1/auth/mot-de-passe-oublie`) ; il manquait la porte.
 *
 * LA RÉPONSE EST NEUTRE, à l'écran comme sur le réseau : le serveur répond la
 * même chose que l'adresse existe ou non, et cet écran aussi. Le message ne
 * dit jamais « aucun compte » — il dit ce qui se passe SI un compte existe.
 * Seule une adresse mal formée est refusée (c'est une faute de forme, pas une
 * information).
 */
export const MESSAGE_LIEN_ENVOYE =
  'Si un compte Silex est associé à cette adresse, un courriel vient d’y être envoyé avec un lien pour choisir un nouveau mot de passe. Le lien est valable une heure.';

export type IssueDemande = 'envoye' | 'adresse_invalide' | 'trop_de_demandes' | 'echec';

/** Le geste, hors du rendu : une issue par cas que l'écran sait dire. */
export async function demanderLienReinitialisation(
  email: string,
  appeler: (email: string) => Promise<unknown> = api.motDePasseOublie,
): Promise<IssueDemande> {
  const adresse = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adresse)) return 'adresse_invalide';
  try {
    await appeler(adresse);
    return 'envoye';
  } catch (err) {
    const status = (err as { status?: number } | null)?.status;
    if (status === 422) return 'adresse_invalide';
    if (status === 429) return 'trop_de_demandes';
    return 'echec';
  }
}

const MESSAGES_ECHEC: Record<Exclude<IssueDemande, 'envoye'>, string> = {
  adresse_invalide: 'Cette adresse courriel n’est pas valide.',
  trop_de_demandes: 'Trop de demandes depuis cet appareil. Réessayez dans quinze minutes.',
  echec: 'La demande n’a pas pu être envoyée. Vérifiez votre connexion réseau.',
};

export function MotDePasseOublie({
  emailInitial = '',
  onRetour,
}: {
  emailInitial?: string;
  onRetour: () => void;
}): ReactNode {
  const [email, setEmail] = useState(emailInitial);
  const [issue, setIssue] = useState<IssueDemande | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEnCours(true);
    setIssue(await demanderLienReinitialisation(email));
    setEnCours(false);
  }

  return (
    <form className="auth-card" onSubmit={soumettre} noValidate aria-label="Mot de passe oublié">
      <div className="auth-card__brand">
        Silex
        <span className="brand-dot" aria-hidden="true" />
      </div>
      <h1 style={{ fontSize: 18, margin: '8px 0' }}>Mot de passe oublié</h1>

      {issue === 'envoye' ? (
        <p className="form-note" role="status">
          {MESSAGE_LIEN_ENVOYE}
        </p>
      ) : (
        <>
          <p className="form-note">
            Indiquez l’adresse courriel de votre compte. Nous y enverrons un lien pour choisir un
            nouveau mot de passe.
          </p>
          {issue ? (
            <p className="form-error" role="alert">
              {MESSAGES_ECHEC[issue]}
            </p>
          ) : null}
          <div className="field">
            <label htmlFor="oubli-email">Adresse courriel</label>
            <input
              id="oubli-email"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {/* AB#884 — le défi anti-robot, seulement avec une clé de site. */}
          <DefiAntiRobot />
          <button type="submit" className="btn" disabled={enCours}>
            {enCours ? 'Envoi…' : 'Recevoir le lien'}
          </button>
        </>
      )}

      <p style={{ marginTop: 16 }}>
        <button type="button" className="btn btn--quiet btn--sm" onClick={onRetour}>
          Retour à la connexion
        </button>
      </p>
    </form>
  );
}
